import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { MapPin, Plane, ExternalLink } from 'lucide-react';
import { weddingData } from '../data';

export const VenueSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden" data-testid="venue-section">
      {/* Parallax Background */}
      <motion.div className="absolute inset-0" style={{ y }}>
        <img
          src={weddingData.venue.image}
          alt="The Shahanshahi Resort"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
      </motion.div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-6 py-20 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="max-w-4xl text-center text-white space-y-8 md:space-y-12"
        >
          {/* Overline */}
          <div className="flex items-center justify-center gap-4">
            <div className="h-px w-12 bg-white/30"></div>
            <p className="text-xs uppercase tracking-widest text-white/60">The Venue</p>
            <div className="h-px w-12 bg-white/30"></div>
          </div>

          {/* Title */}
          <h2 className="font-elegant text-4xl md:text-7xl lg:text-8xl leading-tight">
            The Shahanshahi
          </h2>

          {/* Description */}
          <p className="text-base md:text-2xl text-white/80 font-light leading-relaxed max-w-2xl mx-auto">
            {weddingData.venue.description}
          </p>

          {/* Details */}
          <div className="pt-4 md:pt-8 space-y-4 md:space-y-6">
            <div className="flex items-center justify-center gap-3 text-sm md:text-lg">
              <MapPin className="w-4 h-4 md:w-5 md:h-5 text-white/60 flex-shrink-0" />
              <span className="font-light">{weddingData.venue.address}</span>
            </div>
            <div className="flex items-center justify-center gap-3 text-xs md:text-sm text-white/60 uppercase tracking-widest">
              <Plane className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
              <span>Nearest Airport: {weddingData.venue.nearestAirport}</span>
            </div>
          </div>

          {/* Links */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 pt-2 md:pt-4">
            <a
              href={weddingData.venue.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="venue-map-link"
              className="inline-flex items-center gap-2 px-6 md:px-8 py-3 border border-white/30 text-white hover:bg-white/10 transition-all duration-300 font-light uppercase tracking-widest text-xs md:text-sm w-full sm:w-auto justify-center"
            >
              <MapPin className="w-4 h-4" />
              View on Map
            </a>
            <a
              href={weddingData.venue.website}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="venue-website-link"
              className="inline-flex items-center gap-2 px-6 md:px-8 py-3 border border-white/30 text-white hover:bg-white/10 transition-all duration-300 font-light uppercase tracking-widest text-xs md:text-sm w-full sm:w-auto justify-center"
            >
              <ExternalLink className="w-4 h-4" />
              Resort Website
            </a>
          </div>

          {/* Decorative Line */}
          <div className="flex items-center justify-center pt-4 md:pt-8">
            <div className="h-px w-24 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
